// Phase 14 — high-fidelity Breakdance copy from a Code Mode subtree
// capture. Where lib/breakdance.ts maps a single flat HTML snippet,
// this module walks the CapturedNode tree the overlay produced inside
// the real rendered page (computed styles, geometry, absolutized URLs)
// and reconstructs a *structured* Breakdance element: Section → Div →
// Heading/Text/Button children, instead of one CodeBlock blob.
//
// Pure JSON-in / JSON-out — no DOM dependency — so it's smoke-testable
// in plain node with fixture trees.
//
// ── Design-property translation status ──────────────────────────────
// TODO(step-0): the per-element `properties.design` paths are pending
// the user's styled Breakdance samples (styled Section, flex Div,
// Button, Heading/Text typography, Image-from-URL). Until those lock
// the schema, `designFor()` emits only the shapes confirmed by the
// unstyled samples (Section layout_v2). Structure, text content, links
// and nesting already transfer; visual styling lands when designFor is
// filled in. Filling it in should not require touching the mapping
// rules below.

import type { CapturedNode } from "@clarity/shared";
import { idGen, ROOT_PARENT_ID, type BDElement } from "./breakdance";

export function treeToBreakdanceClipboard(tree: CapturedNode): string {
  const nextId = idGen();
  const element =
    mapNode(tree, ROOT_PARENT_ID, nextId, true) ??
    // Whole tree mapped to nothing (e.g. an empty decorative div) —
    // fall back to an empty Div so the paste still succeeds.
    divElement([], null, ROOT_PARENT_ID, nextId);
  return JSON.stringify({ element });
}

// ---------- recursive mapping ----------

const CONTAINER_TAGS = new Set([
  "section", "div", "header", "footer", "main", "article", "aside",
  "nav", "figure", "li", "span",
]);

const EMBED_TAGS = new Set([
  "svg", "video", "iframe", "canvas", "form", "table", "picture",
]);

function mapNode(
  n: CapturedNode,
  parentId: number,
  nextId: () => number,
  isRoot: boolean,
): BDElement | null {
  const tag = n.tag;

  if (/^h[1-6]$/.test(tag)) {
    return headingElement(n, tag, parentId, nextId);
  }
  if (tag === "p" || tag === "blockquote") {
    if (n.html) return richTextElement(n.html, n, parentId, nextId);
    if (n.text) return textElement(n.text, n, parentId, nextId);
    return null;
  }
  if (tag === "ul" || tag === "ol") {
    if (n.html) return richTextElement(n.html, n, parentId, nextId);
    return null;
  }
  if (tag === "button") {
    return buttonElement(n, parentId, nextId);
  }
  if (tag === "a") {
    const isButtonLike = n.classes.some((c) => /\bbtn\b|button/i.test(c));
    if (isButtonLike) return buttonElement(n, parentId, nextId);
    if (n.children.length === 0) {
      if (!n.text && !n.href) return null;
      return textLinkElement(n, parentId, nextId);
    }
    // Anchor wrapping structure (card links etc.) — keep the children,
    // drop the wrapper link. TODO(step-0): Div may support a link
    // setting; if the samples reveal one, attach n.href here.
    return containerElement(n, parentId, nextId, isRoot);
  }
  if (tag === "img") {
    // TODO(step-0): swap to a native Image element if the user's sample
    // confirms Image2 accepts an external URL. Until then a CodeBlock
    // with a plain <img> renders identically.
    if (!n.src) return null;
    const alt = n.alt ? ` alt="${escapeAttr(n.alt)}"` : "";
    return codeBlockElement(
      `<img src="${escapeAttr(n.src)}"${alt} style="max-width:100%">`,
      parentId,
      nextId,
    );
  }
  if (EMBED_TAGS.has(tag)) {
    if (!n.html) return null;
    return codeBlockElement(n.html, parentId, nextId);
  }
  if (CONTAINER_TAGS.has(tag)) {
    // Leaf container with only text → Text. Empty leaf → skip.
    if (n.children.length === 0) {
      if (n.text) return textElement(n.text, n, parentId, nextId);
      return null;
    }
    return containerElement(n, parentId, nextId, isRoot);
  }

  // Unknown tag: recurse if it has children, salvage text otherwise.
  if (n.children.length > 0) return containerElement(n, parentId, nextId, isRoot);
  if (n.text) return textElement(n.text, n, parentId, nextId);
  return null;
}

function containerElement(
  n: CapturedNode,
  parentId: number,
  nextId: () => number,
  isRoot: boolean,
): BDElement | null {
  // Root containers become a Section so a page-level paste lands as a
  // proper Breakdance section; nested containers become Divs.
  const asSection = isRoot && (n.tag === "section" || isSectionLike(n));
  const el = asSection
    ? sectionElement(n, parentId, nextId)
    : divElement([], designFor(n, "div"), parentId, nextId);

  for (const child of n.children) {
    const mapped = mapNode(child, el.id, nextId, false);
    if (mapped) el.children.push(mapped);
  }

  // A container whose every child mapped to nothing and which has no
  // text of its own adds nothing — skip it (unless it's the root, where
  // returning something keeps the paste valid).
  if (!isRoot && el.children.length === 0 && !n.text) return null;
  if (el.children.length === 0 && n.text) {
    el.children.push(textElement(n.text, n, el.id, nextId));
  }
  return el;
}

function isSectionLike(n: CapturedNode): boolean {
  // Heuristic for root divs that are really page sections: full-bleed-ish
  // width and structural children.
  return n.children.length > 0 && n.rect.width >= 600;
}

// ---------- element builders (plain-data variants) ----------

function headingElement(
  n: CapturedNode,
  tag: string,
  parentId: number,
  nextId: () => number,
): BDElement {
  return {
    id: nextId(),
    data: {
      type: "EssentialElements\\Heading",
      properties: withDesign(
        { content: { content: { text: n.text ?? "", tags: tag } } },
        designFor(n, "heading"),
      ),
    },
    children: [],
    _parentId: parentId,
  };
}

function textElement(
  text: string,
  n: CapturedNode | null,
  parentId: number,
  nextId: () => number,
): BDElement {
  return {
    id: nextId(),
    data: {
      type: "EssentialElements\\Text",
      properties: withDesign(
        { content: { content: { text } } },
        n ? designFor(n, "text") : null,
      ),
    },
    children: [],
    _parentId: parentId,
  };
}

function richTextElement(
  html: string,
  n: CapturedNode,
  parentId: number,
  nextId: () => number,
): BDElement {
  return {
    id: nextId(),
    data: {
      type: "EssentialElements\\RichText",
      properties: withDesign(
        { content: { content: { text: html } } },
        designFor(n, "richtext"),
      ),
    },
    children: [],
    _parentId: parentId,
  };
}

function buttonElement(
  n: CapturedNode,
  parentId: number,
  nextId: () => number,
): BDElement {
  const link = n.href ? { type: "url", url: n.href } : undefined;
  return {
    id: nextId(),
    data: {
      type: "EssentialElements\\Button",
      properties: withDesign(
        {
          content: {
            content: {
              text: n.text || "Button",
              ...(link ? { link } : {}),
            },
          },
        },
        designFor(n, "button") ?? { button: { style: "custom" } },
      ),
    },
    children: [],
    _parentId: parentId,
  };
}

function textLinkElement(
  n: CapturedNode,
  parentId: number,
  nextId: () => number,
): BDElement {
  return {
    id: nextId(),
    data: {
      type: "EssentialElements\\TextLink",
      properties: {
        content: {
          content: {
            link: { type: "url", url: n.href ?? "" },
            text: n.text || n.href || "Link",
          },
        },
      },
    },
    children: [],
    _parentId: parentId,
  };
}

function codeBlockElement(
  html: string,
  parentId: number,
  nextId: () => number,
): BDElement {
  return {
    id: nextId(),
    data: {
      type: "EssentialElements\\CodeBlock",
      properties: { content: { content: { php_code: html } } },
    },
    children: [],
    _parentId: parentId,
  };
}

function sectionElement(
  n: CapturedNode,
  parentId: number,
  nextId: () => number,
): BDElement {
  return {
    id: nextId(),
    data: {
      type: "EssentialElements\\Section",
      properties: withDesign(
        null,
        designFor(n, "section") ?? { layout_v2: { layout: "vertical" } },
      ),
    },
    children: [],
    _parentId: parentId,
  };
}

function divElement(
  children: BDElement[],
  design: Record<string, unknown> | null,
  parentId: number,
  nextId: () => number,
): BDElement {
  return {
    id: nextId(),
    data: {
      type: "EssentialElements\\Div",
      properties: withDesign(null, design),
    },
    children,
    _parentId: parentId,
  };
}

// ---------- design translation ----------
//
// Property paths locked from the user's styled Breakdance sample
// (section + header row + grid of bordered cards). Notable asymmetry:
// Section padding lives at design.spacing.padding while Div padding is
// design.container.padding.padding — don't "simplify" these to match.
//
// Still TODO (samples pending): Button background/padding/radius paths,
// Image2 external-URL support. Buttons currently emit only
// button.style:"custom" + typography color.

type DesignKind = "section" | "div" | "heading" | "text" | "richtext" | "button";

function designFor(
  n: CapturedNode,
  kind: DesignKind,
): Record<string, unknown> | null {
  switch (kind) {
    case "section":
      return sectionDesign(n);
    case "div":
      return divDesign(n);
    case "heading":
    case "text":
    case "richtext":
      return typographyDesign(n);
    case "button":
      return buttonDesign(n);
  }
}

function sectionDesign(n: CapturedNode): Record<string, unknown> {
  const design: Record<string, unknown> = {
    layout_v2: {
      layout: "vertical",
      ...(isFlexColumn(n) && gapPx(n) !== null
        ? { v_gap: bpPx(gapPx(n)!) }
        : {}),
    },
  };
  const bg = solidBackground(n);
  if (bg) design.background = { color: bg };
  const padding = paddingBox(n);
  if (padding) design.spacing = { padding: { breakpoint_base: padding } };
  return design;
}

function divDesign(n: CapturedNode): Record<string, unknown> | null {
  const design: Record<string, unknown> = {};

  // Layout: grid → grid; flex row → horizontal; flex column → vertical.
  const display = n.styles["display"] ?? "";
  if (display === "grid" || display === "inline-grid") {
    const cols = gridColumnCount(n);
    const gap = gapPx(n);
    design.layout_v2 = {
      layout: "grid",
      ...(cols ? { g_items_per_row: { breakpoint_base: cols } } : {}),
      ...(gap !== null ? { g_space_between_items: bpPx(gap) } : {}),
    };
  } else if (display === "flex" || display === "inline-flex") {
    if (isFlexColumn(n)) {
      const gap = gapPx(n);
      design.layout_v2 = {
        layout: "vertical",
        ...(gap !== null ? { v_gap: bpPx(gap) } : {}),
      };
    } else {
      const justify = passAlign(n.styles["justify-content"]);
      const align = passAlign(n.styles["align-items"]);
      const gap = gapPx(n);
      design.layout_v2 = {
        layout: "horizontal",
        ...(justify ? { h_align: { breakpoint_base: justify } } : {}),
        ...(align ? { h_vertical_align: { breakpoint_base: align } } : {}),
        // Horizontal gap key inferred from the v_gap naming convention —
        // not yet confirmed by a sample; harmlessly ignored if wrong.
        ...(gap !== null ? { h_gap: bpPx(gap) } : {}),
      };
    }
  }

  // Card chrome: padding / radius / border live under container.
  const container: Record<string, unknown> = {};
  const padding = paddingBox(n);
  if (padding) container.padding = { padding: { breakpoint_base: padding } };

  const borders: Record<string, unknown> = {};
  const radius = radiusPx(n);
  if (radius !== null) {
    const r = pxObj(radius);
    borders.radius = {
      breakpoint_base: {
        all: r,
        topLeft: r,
        topRight: r,
        bottomLeft: r,
        bottomRight: r,
        editMode: "all",
      },
    };
  }
  const border = borderSide(n);
  if (border) {
    borders.border = {
      breakpoint_base: { top: border, bottom: border, left: border, right: border },
    };
  }
  if (Object.keys(borders).length > 0) container.borders = borders;
  if (Object.keys(container).length > 0) design.container = container;

  const bg = solidBackground(n);
  if (bg) design.background = { color: bg };

  return Object.keys(design).length > 0 ? design : null;
}

function typographyDesign(n: CapturedNode): Record<string, unknown> | null {
  const custom: Record<string, unknown> = {};

  const fontSize = pxNumber(n.styles["font-size"]);
  if (fontSize !== null) custom.fontSize = bpPx(fontSize);

  const weight = (n.styles["font-weight"] ?? "").trim();
  // 400 is the browser default — pinning it explicitly would override
  // Breakdance theme weights for no visual gain.
  if (weight && weight !== "400" && weight !== "normal") {
    custom.fontWeight = { breakpoint_base: weight };
  }

  const lineHeight = pxNumber(n.styles["line-height"]);
  if (lineHeight !== null) custom.lineHeight = bpPx(lineHeight);

  const advanced: Record<string, unknown> = {};
  const transform = (n.styles["text-transform"] ?? "").trim();
  if (transform && transform !== "none") {
    advanced.textTransform = { breakpoint_base: transform };
  }
  if (Object.keys(advanced).length > 0) custom.advanced = advanced;

  const typography: Record<string, unknown> = {};
  if (Object.keys(custom).length > 0) {
    typography.typography = { custom: { customTypography: custom } };
  }
  const color = colorValue(n.styles["color"]);
  if (color) typography.color = { breakpoint_base: color };

  if (Object.keys(typography).length === 0) return null;
  return { typography };
}

function buttonDesign(n: CapturedNode): Record<string, unknown> {
  // TODO(step-0): background / padding / border-radius paths pending a
  // styled Button sample. Until then we pin the custom style flag and
  // text color so at least the label color transfers.
  const design: Record<string, unknown> = { button: { style: "custom" } };
  const color = colorValue(n.styles["color"]);
  if (color) {
    design.typography = { color: { breakpoint_base: color } };
  }
  return design;
}

// ---------- style-value helpers ----------

type PxObj = { number: number; unit: "px"; style: string };

function pxObj(value: number): PxObj {
  const rounded = Math.round(value * 10) / 10;
  return { number: rounded, unit: "px", style: `${rounded}px` };
}

function bpPx(value: number): { breakpoint_base: PxObj } {
  return { breakpoint_base: pxObj(value) };
}

function pxNumber(raw: string | undefined): number | null {
  if (!raw) return null;
  const m = /^(-?\d+(?:\.\d+)?)px$/.exec(raw.trim());
  if (!m || !m[1]) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : null;
}

function isFlexColumn(n: CapturedNode): boolean {
  const display = n.styles["display"] ?? "";
  if (display !== "flex" && display !== "inline-flex") return false;
  return (n.styles["flex-direction"] ?? "").startsWith("column");
}

function gapPx(n: CapturedNode): number | null {
  // Computed `gap` is "row column" or a single value; row-gap/column-gap
  // are the reliable per-axis reads. Prefer row-gap, fall back to
  // column-gap, then the shorthand's first token.
  for (const key of ["row-gap", "column-gap"]) {
    const v = pxNumber(n.styles[key]);
    if (v !== null && v > 0) return v;
  }
  const first = (n.styles["gap"] ?? "").split(/\s+/)[0];
  const v = pxNumber(first);
  return v !== null && v > 0 ? v : null;
}

function gridColumnCount(n: CapturedNode): number | null {
  // Computed grid-template-columns resolves repeat() into an explicit
  // track list ("228px 228px 228px 228px") — token count = columns.
  const raw = (n.styles["grid-template-columns"] ?? "").trim();
  if (!raw || raw === "none") return null;
  const count = raw.split(/\s+/).filter(Boolean).length;
  return count > 0 ? count : null;
}

function passAlign(raw: string | undefined): string | null {
  const v = (raw ?? "").trim();
  // normal/stretch are the CSS defaults — emitting them just adds noise.
  if (!v || v === "normal" || v === "stretch") return null;
  if (v === "start") return "flex-start";
  if (v === "end") return "flex-end";
  return v;
}

function solidBackground(n: CapturedNode): string | null {
  return colorValue(n.styles["background-color"]);
}

function colorValue(raw: string | undefined): string | null {
  const v = (raw ?? "").trim();
  if (!v || v === "transparent" || v === "rgba(0, 0, 0, 0)") return null;
  return rgbToHex(v) ?? v;
}

function rgbToHex(value: string): string | null {
  const m =
    /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)$/.exec(
      value,
    );
  if (!m) return null;
  const hex = (s: string | undefined) =>
    Math.max(0, Math.min(255, Number(s ?? 0)))
      .toString(16)
      .padStart(2, "0");
  const alpha = m[4] !== undefined ? Number(m[4]) : 1;
  const base = `#${hex(m[1])}${hex(m[2])}${hex(m[3])}`;
  if (alpha >= 1) return base;
  const a = Math.round(alpha * 255)
    .toString(16)
    .padStart(2, "0");
  return base + a;
}

function paddingBox(
  n: CapturedNode,
): Record<string, PxObj> | null {
  const top = pxNumber(n.styles["padding-top"]) ?? 0;
  const right = pxNumber(n.styles["padding-right"]) ?? 0;
  const bottom = pxNumber(n.styles["padding-bottom"]) ?? 0;
  const left = pxNumber(n.styles["padding-left"]) ?? 0;
  if (top === 0 && right === 0 && bottom === 0 && left === 0) return null;
  return {
    top: pxObj(top),
    right: pxObj(right),
    bottom: pxObj(bottom),
    left: pxObj(left),
  };
}

function radiusPx(n: CapturedNode): number | null {
  // Computed border-radius may be "14px" or a multi-value list; take the
  // first corner for the uniform "all" edit mode.
  const first = (n.styles["border-radius"] ?? "").split(/\s+/)[0];
  const v = pxNumber(first);
  return v !== null && v > 0 ? v : null;
}

function borderSide(
  n: CapturedNode,
): { width: PxObj; color: string; style: string } | null {
  const width = pxNumber(n.styles["border-top-width"]);
  const style = (n.styles["border-top-style"] ?? "").trim();
  if (!width || width <= 0 || !style || style === "none") return null;
  const color = colorValue(n.styles["border-top-color"]) ?? "#000000";
  return { width: pxObj(width), color, style };
}

function withDesign(
  base: Record<string, unknown> | null,
  design: Record<string, unknown> | null,
): Record<string, unknown> | null {
  if (!base && !design) return null;
  return { ...(base ?? {}), ...(design ? { design } : {}) };
}

function escapeAttr(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
}
